import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Pagination,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { typographyTokens } from '../../../../theme/typographyTokens';
import {
  DEFAULT_SEP_PROMPT_SETTINGS,
  useGetSepPromptSettingsHistoryQuery,
  useGetSepPromptSettingsQuery,
  usePreviewSepPromptSettingsMutation,
  useResetSepPromptSettingsMutation,
  useUpdateSepPromptSettingsMutation,
} from '../../../../services/sepPromptSettingsSlice/sepPromptSettingsSlice';
import {
  SepPromptSettingHistoryItem,
  SepPromptSettingValues,
} from '../../../../../shared/types/SepPromptSetting';
import { useGetAllSEPIDsQuery } from '../../../../services/sepSlice';

interface AiSummarySettingsTabProps {
  onDirtyChange: (dirty: boolean) => void;
}

interface SepOption {
  id: number;
  name: string;
}

const promptFieldSx = {
  '& textarea': { fontFamily: 'monospace', fontSize: 13 },
};

const HISTORY_PAGE_SIZE = 5;

const toPromptPreview = (prompt: string): string => {
  const normalized = prompt.replace(/\s+/g, ' ').trim();
  return normalized.length > 180 ? `${normalized.slice(0, 177)}...` : normalized;
};

const formatDateTime = (value: string): string =>
  new Date(value).toLocaleString();

const sourceChips = (setting: SepPromptSettingValues) => [
  { label: 'Description', enabled: setting.includeDescription },
  { label: 'Intake', enabled: setting.includeIntakeResponses },
  { label: 'Attachments', enabled: setting.includeAttachments },
];

const AiSummarySettingsTab: React.FC<AiSummarySettingsTabProps> = ({
  onDirtyChange,
}) => {
  const { data, isLoading, isError } = useGetSepPromptSettingsQuery();
  const { data: sepIDs = [], isLoading: isLoadingSeps } = useGetAllSEPIDsQuery();
  const { data: history = [], isFetching: historyLoading } =
    useGetSepPromptSettingsHistoryQuery();
  const [updateSettings, updateState] = useUpdateSepPromptSettingsMutation();
  const [resetSettings, resetState] = useResetSepPromptSettingsMutation();
  const [previewSettings, previewState] = usePreviewSepPromptSettingsMutation();
  const [values, setValues] = useState<SepPromptSettingValues>(
    DEFAULT_SEP_PROMPT_SETTINGS
  );
  const [version, setVersion] = useState<number>(1);
  const [sepID, setSepID] = useState('');
  const [preview, setPreview] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [viewedVersion, setViewedVersion] =
    useState<SepPromptSettingHistoryItem | null>(null);
  const [historyPage, setHistoryPage] = useState(1);
  const [dirty, setDirty] = useState(false);

  const sepOptions: SepOption[] = Array.isArray(sepIDs)
    ? sepIDs
        .map((sep: any) => {
          const id = typeof sep === 'number' ? sep : Number(sep.id ?? sep.dataValues?.id);
          const name = typeof sep === 'number'
            ? `SEP ${id}`
            : sep.name ?? sep.dataValues?.name ?? `SEP ${id}`;
          return { id, name };
        })
        .filter((sep) => Number.isInteger(sep.id) && sep.id > 0)
    : [];

  useEffect(() => {
    if (!data) return;
    setValues({
      systemPrompt: data.active.systemPrompt,
      includeDescription: data.active.includeDescription,
      includeIntakeResponses: data.active.includeIntakeResponses,
      includeAttachments: data.active.includeAttachments,
    });
    setVersion(data.active.version);
    setDirty(false);
  }, [data]);

  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);

  const historyPageCount = Math.max(1, Math.ceil(history.length / HISTORY_PAGE_SIZE));
  const paginatedHistory = history.slice(
    (historyPage - 1) * HISTORY_PAGE_SIZE,
    historyPage * HISTORY_PAGE_SIZE
  );

  useEffect(() => {
    setHistoryPage((currentPage) => Math.min(currentPage, historyPageCount));
  }, [historyPageCount]);

  const updateValue = <K extends keyof SepPromptSettingValues>(
    key: K,
    value: SepPromptSettingValues[K]
  ) => {
    setValues((current) => ({ ...current, [key]: value }));
    setDirty(true);
    setSuccessMessage('');
  };

  const handleSave = async () => {
    setErrorMessage('');
    setSuccessMessage('');
    try {
      const saved = await updateSettings(values).unwrap();
      setVersion(saved.version);
      setDirty(false);
      setSuccessMessage('AI summary settings saved.');
    } catch (error: any) {
      setErrorMessage(error?.data?.error || 'Unable to save AI summary settings.');
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset the AI summary settings to the defaults?')) return;
    setErrorMessage('');
    try {
      const reset = await resetSettings().unwrap();
      setValues({
        systemPrompt: reset.systemPrompt,
        includeDescription: reset.includeDescription,
        includeIntakeResponses: reset.includeIntakeResponses,
        includeAttachments: reset.includeAttachments,
      });
      setVersion(reset.version);
      setDirty(false);
      setSuccessMessage('AI summary settings reset to defaults.');
    } catch (error: any) {
      setErrorMessage(error?.data?.error || 'Unable to reset AI summary settings.');
    }
  };

  const handlePreview = async () => {
    const parsedSepID = Number(sepID);
    if (!Number.isInteger(parsedSepID) || parsedSepID <= 0) {
      setErrorMessage('Enter a valid SEP ID to run a preview.');
      return;
    }
    setErrorMessage('');
    try {
      const result = await previewSettings({ ...values, sepID: parsedSepID }).unwrap();
      setPreview(result.summary);
    } catch (error: any) {
      setErrorMessage(error?.data?.error || 'Unable to run the preview.');
    }
  };

  const loadHistoryVersion = (historyVersion: number) => {
    const selected = history.find((item) => item.version === historyVersion);
    if (!selected) return;
    setValues({
      systemPrompt: selected.systemPrompt,
      includeDescription: selected.includeDescription,
      includeIntakeResponses: selected.includeIntakeResponses,
      includeAttachments: selected.includeAttachments,
    });
    setDirty(true);
    setSuccessMessage(`Version ${selected.version} loaded into the form.`);
  };

  if (isLoading) {
    return <CircularProgress size={24} />;
  }

  return (
    <Box sx={{ p: 3, width: '100%', boxSizing: 'border-box' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={2} mb={3}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: typographyTokens.fontWeights.bold }}>
            AI summary settings
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            Configure the prompt and source data used for newly generated SEP summaries.
          </Typography>
        </Box>
        <Chip label={`Active: version ${version}`} color="primary" variant="outlined" />
      </Stack>

      <Stack spacing={2}>
        <TextField
          label="System prompt"
          multiline
          minRows={9}
          fullWidth
          value={values.systemPrompt}
          onChange={(event) => updateValue('systemPrompt', event.target.value)}
          sx={promptFieldSx}
          helperText={`${values.systemPrompt.length}/4000 characters`}
        />
        <Box>
          <Typography sx={{ fontWeight: typographyTokens.fontWeights.bold, mb: 1 }}>
            Data sources
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }}>
            <FormControlLabel
              control={<Switch checked={values.includeDescription} onChange={(e) => updateValue('includeDescription', e.target.checked)} />}
              label="Include description"
            />
            <FormControlLabel
              control={<Switch checked={values.includeIntakeResponses} onChange={(e) => updateValue('includeIntakeResponses', e.target.checked)} />}
              label="Include intake responses"
            />
            <FormControlLabel
              control={<Switch checked={values.includeAttachments} onChange={(e) => updateValue('includeAttachments', e.target.checked)} />}
              label="Include attachment names"
            />
          </Stack>
        </Box>

        <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2 }}>
          <Typography variant="h6">Test with sample request</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1, width: '100%' }}>
            <Autocomplete
              options={sepOptions}
              loading={isLoadingSeps}
              value={sepOptions.find((sep) => sep.id === Number(sepID)) || null}
              onChange={(_event, value) => {
                setSepID(value ? String(value.id) : '');
                setPreview('');
              }}
              getOptionLabel={(option) => `${option.id} - ${option.name}`}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              autoHighlight
              openOnFocus
              noOptionsText="No matching SEP found"
              sx={{ flex: 1, minWidth: 0 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search SEP"
                  placeholder="Search by SEP ID or name"
                  size="small"
                />
              )}
            />
            <Button
              variant="outlined"
              onClick={() => void handlePreview()}
              disabled={!sepID || previewState.isLoading}
              sx={{ width: { xs: '100%', sm: 180 }, flexShrink: 0 }}
            >
              {previewState.isLoading ? <CircularProgress size={18} /> : 'Run preview'}
            </Button>
          </Stack>
          <TextField
            label="Preview output"
            multiline
            minRows={4}
            fullWidth
            value={preview}
            InputProps={{ readOnly: true }}
            sx={{ mt: 2 }}
          />
        </Box>

        <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2 }}>
          <Typography variant="h6">Prompt version history</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
            Each save creates a new version. Only the active version is used for newly
            generated summaries.
          </Typography>

          {historyLoading ? (
            <CircularProgress size={20} />
          ) : history.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No saved versions yet.
            </Typography>
          ) : (
            <TableContainer sx={{ width: '100%', maxHeight: 420 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 90 }}>Version</TableCell>
                    <TableCell sx={{ width: 110 }}>Status</TableCell>
                    <TableCell>Prompt preview</TableCell>
                    <TableCell sx={{ width: 210 }}>Data sources</TableCell>
                    <TableCell sx={{ width: 190 }}>Created</TableCell>
                    <TableCell sx={{ width: 150 }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedHistory.map((item) => (
                    <TableRow
                      key={item.id}
                      hover
                      sx={item.isActive ? { backgroundColor: 'action.hover' } : undefined}
                    >
                      <TableCell sx={{ fontWeight: typographyTokens.fontWeights.bold }}>
                        v{item.version}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={item.isActive ? 'Active' : 'Inactive'}
                          color={item.isActive ? 'success' : 'default'}
                          variant={item.isActive ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 0 }}>
                        <Typography
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: 12,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {toPromptPreview(item.systemPrompt)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.systemPrompt.length} chars
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          {sourceChips(item).map((source) => (
                            <Chip
                              key={source.label}
                              size="small"
                              label={source.label}
                              color={source.enabled ? 'primary' : 'default'}
                              variant={source.enabled ? 'filled' : 'outlined'}
                              sx={{ opacity: source.enabled ? 1 : 0.6 }}
                            />
                          ))}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDateTime(item.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Button size="small" onClick={() => setViewedVersion(item)}>
                          View
                        </Button>
                        <Button size="small" onClick={() => loadHistoryVersion(item.version)}>
                          Load
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {historyPageCount > 1 && (
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={1}
                  sx={{ p: 1.5 }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Showing {(historyPage - 1) * HISTORY_PAGE_SIZE + 1}-
                    {Math.min(historyPage * HISTORY_PAGE_SIZE, history.length)} of{' '}
                    {history.length} versions
                  </Typography>
                  <Pagination
                    count={historyPageCount}
                    page={historyPage}
                    onChange={(_event, page) => setHistoryPage(page)}
                    color="primary"
                    size="small"
                    showFirstButton
                    showLastButton
                  />
                </Stack>
              )}
            </TableContainer>
          )}
        </Box>
      </Stack>

      <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1} sx={{ mt: 3 }}>
        <Button onClick={handleReset} disabled={resetState.isLoading}>Reset to default</Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="contained" onClick={handleSave} disabled={updateState.isLoading || !dirty}>
          {updateState.isLoading ? <CircularProgress size={18} /> : 'Save changes'}
        </Button>
      </Stack>

      <Dialog
        open={Boolean(viewedVersion)}
        onClose={() => setViewedVersion(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Version {viewedVersion?.version}
          {viewedVersion?.isActive ? ' (active)' : ''}
        </DialogTitle>
        <DialogContent dividers>
          {viewedVersion && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Created {formatDateTime(viewedVersion.createdAt)}
                </Typography>
                <Stack direction="row" spacing={0.5} sx={{ mt: 1 }}>
                  {sourceChips(viewedVersion).map((source) => (
                    <Chip
                      key={source.label}
                      size="small"
                      label={source.label}
                      color={source.enabled ? 'primary' : 'default'}
                      variant={source.enabled ? 'filled' : 'outlined'}
                    />
                  ))}
                </Stack>
              </Box>
              <Divider />
              <TextField
                label="System prompt"
                multiline
                fullWidth
                minRows={10}
                maxRows={20}
                value={viewedVersion.systemPrompt}
                InputProps={{ readOnly: true }}
                sx={promptFieldSx}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {viewedVersion && (
            <Button
              onClick={() => {
                loadHistoryVersion(viewedVersion.version);
                setViewedVersion(null);
              }}
            >
              Load into form
            </Button>
          )}
          <Button onClick={() => setViewedVersion(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AiSummarySettingsTab;